/**
 * layers.ts — the portal's fixed/sticky stacking contract, in ONE place.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: tracking A1 (consent).
 *
 * WHY a module and not four inline numbers: the layers below are not decoration,
 * they encode a SAFETY ordering, and an ordering spread across three files is an
 * ordering nobody can check. Two of these constants are load-bearing enough that
 * a unit test asserts the relation between them (see ConsentBanner.spec.tsx).
 *
 * The order, bottom to top:
 *
 *   (10) — the journey's sticky phone action row (tokens.css, `.gps-jux-action-row`;
 *     journeyLayout.spec pins it below the two layers that follow).
 *
 *   JOURNEY_JUMP (15) — the q15 agreement step's floating "Go to the agreement
 *     tick" arrow on a phone (2026-09-26). ABOVE page content and the sticky row
 *     (it is placed clear of the row, 9rem up, so it never sits on the row's
 *     button — this only decides who paints on top if they ever meet), and BELOW
 *     everything that must win: the consent scrim (the cookie prompt must cover
 *     it like any other control), the R31 ribbon, and every DialogShell (1000).
 *
 *   CONSENT_SCRIM (40) — the blocking veil the consent modal paints over the
 *     page. It sits BELOW the sticky top chrome ON PURPOSE. `ui/chrome.tsx`
 *     renders the R31 crisis ribbon ("Not for emergencies. Call 999 …") inside
 *     StickyTop, and a scrim above it would dim and swallow clicks on emergency
 *     signposting because a visitor had not yet answered a cookie question.
 *     Nothing about analytics consent may ever sit between a person in crisis
 *     and the 999 line. This number must stay strictly below STICKY_TOP.
 *
 *   STICKY_TOP (50) — the pinned top chrome: the R31 crisis ribbon and, on the
 *     public surfaces, the brand header under it. One wrapper holds both (they
 *     stick as a unit at `top: 0`), so the header rides above the scrim too;
 *     splitting them to dim the header alone would mean re-plumbing the sticky
 *     offsets of live chrome, and the header's only controls are "home" and
 *     "Speak to a clinician now" — the escape hatches, not the page.
 *
 *   CONSENT_RIBBON (60) — the consent modal itself, above the top chrome so the
 *     thing the visitor must answer is never the thing hidden behind a bar.
 *
 *   STAGING_BADGE (max) — the non-prod "Staging Environment" flag. Deliberately
 *     above everything (it must be impossible to mistake stage for production)
 *     and harmless there: `aria-hidden` + `pointer-events: none`, so it covers
 *     no control and never renders on a production host at all.
 */

/**
 * The q15 agreement step's floating jump arrow (phone only). MUST stay above the
 * sticky action row's 10 and below {@link LAYER_CONSENT_SCRIM}.
 */
export const LAYER_JOURNEY_JUMP = 15;

/** The consent modal's page-dimming veil. MUST stay < {@link LAYER_STICKY_TOP}. */
export const LAYER_CONSENT_SCRIM = 40;

/** The pinned top chrome — R31 crisis ribbon (+ brand header on public pages). */
export const LAYER_STICKY_TOP = 50;

/** The consent modal's bottom ribbon — above the top chrome. */
export const LAYER_CONSENT_RIBBON = 60;

/** The non-prod staging flag: above all, but inert (aria-hidden, no pointer events). */
export const LAYER_STAGING_BADGE = 2147483647;
